// src/pages/CustomerPassbook.js
import React, { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import API_BASE_URL from "../../../../utils/config";

// ------- Org / Layout -------
const ORG = {
  name: "WECARE INDIA AGENCY 24",
  addr1: "D20, NEW BAPU NAGAR",
  addr2: "BHILWARA-311001, Rajasthan",
  phone: "Phone: 8800097563",
  gst: "GST NO: 08DQXPS8076J1ZJ",
};

const SCALE = 1;
const fs = (pt) => pt * SCALE;

// ---------- helpers ----------
const fmtINR = (n) =>
  Number.isFinite(Number(n))
    ? new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n))
    : n ?? "-";

const safeISO = (s) => (s && /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : "");
const dmy = (iso) => {
  const s = safeISO(iso);
  if (!s) return "-";
  const [y, m, d] = s.split("-");
  return `${d}-${m}-${y}`;
};
const todayISO = () => new Date().toISOString().slice(0, 10);

const toDMY = (n) => {
  if (!n) return "";
  const m = String(n).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return "";
  const [, y, mm, dd] = m;
  return `${dd}-${mm}-${y}`;
};

// UI helpers
const numClass = "text-right font-mono tabular-nums";
const prettyAmt = (n) => {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v)) return { text: "-", cls: "" };
  const abs = fmtINR(Math.abs(v));
  return v < 0 ? { text: `(${abs})`, cls: "text-red-600" } : { text: abs, cls: "text-gray-900" };
};
const badgeToneClass = (tone) =>
  ({
    gray: "bg-gray-100 text-gray-800",
    blue: "bg-blue-100 text-blue-800",
    red: "bg-red-100 text-red-800",
    green: "bg-green-100 text-green-800",
    emerald: "bg-emerald-100 text-emerald-800",
    purple: "bg-purple-100 text-purple-800",
  }[tone] || "bg-gray-100 text-gray-800");
const typeTone = (t) => {
  const s = String(t || "").toUpperCase();
  if (s.includes("PAY")) return "green";
  if (s.includes("CREDIT")) return "purple";
  if (s.includes("INV")) return "blue";
  if (s.includes("RET") || s.includes("REFUND")) return "emerald";
  return "gray";
};

// FY helpers
function getCurrentFYDates() {
  const t = new Date();
  const y = t.getFullYear();
  const m = t.getMonth() + 1;
  let from, to;
  if (m >= 4) {
    from = new Date(y, 3, 1);
    to = new Date(y + 1, 2, 31);
  } else {
    from = new Date(y - 1, 3, 1);
    to = new Date(y, 2, 31);
  }
  const fmt = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return { fromDate: fmt(from), toDate: fmt(to) };
}
function startOfMonth(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function endOfMonth(d = new Date()) {
  const e = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return `${e.getFullYear()}-${String(e.getMonth() + 1).padStart(2, "0")}-${String(e.getDate()).padStart(2, "0")}`;
}

// ---------- PDF helpers ----------
const ddmmyy = (iso) => {
  if (!iso) return "";
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return "";
  const [, y, mm, dd] = m;
  return `${dd}/${mm}/${String(y).slice(-2)}`;
};
const dmyPretty = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

// Embed Unicode TTF from public/
async function registerTTF(doc, url, name = "NotoSans", style = "normal") {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Font fetch failed: ${res.status}`);
  const buf = await res.arrayBuffer();
  let bin = "";
  const bytes = new Uint8Array(buf);
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  const b64 = btoa(bin);
  doc.addFileToVFS(`${name}.ttf`, b64);
  doc.addFont(`${name}.ttf`, name, style);
  doc.setFont(name, style);
  doc.getTextWidth("0123456789");
}

const moneyINR = (n) =>
  new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n || 0));

const cleanRef = (ref = "") => String(ref).replace(/^(INV-|RECEIPT-|CN-)/i, "");
function particularsLine(row) {
  const t = String(row.type || "").toUpperCase();
  const refClean = cleanRef(row.reference || "");
  const dateShort = ddmmyy(row.date);
  if (t === "INVOICE") return `To  Sales Invoice No. ${refClean}  Dt:${dateShort}`;
  if (t === "PAYMENT") return `By  Receipt No. ${refClean}`;
  if (t === "CREDIT NOTE") return `By  Cr. Note No. ${refClean}`;
  return (row.narration && row.narration.trim()) || (row.reference || "-");
}

// ---------- component ----------
export default function CustomerPassbook() {
  const { fromDate: defFrom, toDate: defTo } = useMemo(getCurrentFYDates, []);
  const [fromDate, setFromDate] = useState(defFrom);
  const [toDate, setToDate] = useState(defTo);

  const { id: customerId } = useParams();

  const [transactions, setTransactions] = useState([]);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [customerName, setCustomerName] = useState("");

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [search, setSearch] = useState("");

  const token = useMemo(() => localStorage.getItem("token"), []);

  // derived sums & closing
  const { sumDebit, sumCredit, closingBalance, closingType } = useMemo(() => {
    const debit = transactions.reduce((s, t) => s + (Number(t.debit) || 0), 0);
    const credit = transactions.reduce((s, t) => s + (Number(t.credit) || 0), 0);
    const lastBal =
      transactions.length > 0 && Number.isFinite(Number(transactions.at(-1).balance))
        ? Number(transactions.at(-1).balance)
        : Number(openingBalance || 0) + debit - credit;
    const type = lastBal >= 0 ? "Dr" : "Cr";
    return { sumDebit: debit, sumCredit: credit, closingBalance: Math.abs(lastBal), closingType: type };
  }, [transactions, openingBalance]);

  // filtered view
  const filtered = useMemo(() => {
    if (!search.trim()) return transactions;
    const q = search.toLowerCase();
    return transactions.filter((t) =>
      [t.type, t.reference, t.narration, toDMY(t.date), fmtINR(t.debit), fmtINR(t.credit), fmtINR(t.balance)]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [transactions, search]);

  const fetchCustomerName = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/customers/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res?.data?.name) setCustomerName(res.data.name);
    } catch {}
  }, [customerId, token]);

  const fetchPassbook = useCallback(async () => {
    if (!fromDate || !toDate) {
      alert("Please select both dates");
      return;
    }
    setErrorMsg("");
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/customers/${customerId}/passbook`, {
        params: { from: fromDate, to: toDate },
        headers: { Authorization: `Bearer ${token}` },
      });
      const ob = Number(res.data?.opening_balance) || 0;
      const txns = Array.isArray(res.data?.transactions) ? res.data.transactions : [];

      setOpeningBalance(ob);
      setTransactions(txns);

      if (res.data?.customer_name) setCustomerName(res.data.customer_name);
      else if (!customerName) fetchCustomerName();
    } catch (err) {
      console.error(err);
      setErrorMsg(err?.response?.data?.error || "Failed to fetch passbook.");
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, customerId, token, customerName, fetchCustomerName]);

  // ---------- quick ranges ----------
  const setFY = () => {
    const { fromDate, toDate } = getCurrentFYDates();
    setFromDate(fromDate);
    setToDate(toDate);
  };
  const setThisMonth = () => {
    const now = new Date();
    setFromDate(startOfMonth(now));
    setToDate(endOfMonth(now));
  };
  const setLastMonth = () => {
    const now = new Date();
    const last = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    setFromDate(startOfMonth(last));
    setToDate(endOfMonth(last));
  };
  const setToday = () => {
    const t = todayISO();
    setFromDate(t);
    setToDate(t);
  };

  // ---------- CSV export ----------
  const downloadCSV = () => {
    const rows = [
      ["Date", "Type", "Reference/Narration", "Debit", "Credit", "Balance"],
      ["", "Opening Balance", "", "", "", openingBalance.toFixed(2)],
      ...transactions.map((t) => [
        toDMY(t.date),
        String(t.type || "").toUpperCase(),
        t.reference || t.narration || "",
        Number(t.debit) > 0 ? Number(t.debit).toFixed(2) : "-",
        Number(t.credit) > 0 ? Number(t.credit).toFixed(2) : "-",
        Number(t.balance).toFixed(2),
      ]),
      ["", `Closing Balance (${closingType})`, "", "", "", closingBalance.toFixed(2)],
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safe = (customerName || `customer_${customerId}`).replace(/[^\w\-]+/g, "_");
    a.href = url;
    a.download = `ledger_${safe}_${fromDate}_to_${toDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ---------- PDF ----------
  const downloadPDF = async () => {
    if (!transactions.length && !openingBalance) {
      alert("Please fetch the statement before downloading.");
      return;
    }

    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const FONT_URL = `${process.env.PUBLIC_URL || ""}/fonts/NotoSans-Regular.ttf`;

    let hasUnicode = true;
    try {
      await registerTTF(doc, FONT_URL);
    } catch {
      hasUnicode = false;
      doc.setFont("helvetica", "normal");
    }

    const L = 36, R = 36, W = doc.internal.pageSize.getWidth(), X2 = W - R;
    let y = 36;

    // Header
    doc.setFontSize(fs(12));
    doc.text(ORG.name, W / 2, y, { align: "center" });
    y += fs(16);
    doc.setFontSize(fs(10));
    doc.text(`${ORG.addr1}`, W / 2, y, { align: "center" });
    y += fs(12);
    doc.text(`${ORG.addr2}`, W / 2, y, { align: "center" });
    y += fs(10);

    y += fs(12);
    const periodStr = `Account Statement from ${ddmmyy(fromDate)} to ${ddmmyy(toDate)}`;
    const printedStr = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    doc.setFontSize(fs(10));
    doc.text(periodStr, W / 2, y, { align: "center" });
    doc.text(printedStr, X2, y, { align: "right" });
    y += fs(14);

    // Party line
    const partyLine = customerName || `#${customerId}`;
    doc.setFontSize(fs(12));
    doc.text(partyLine, L, y);
    y += fs(14);

    // placeholders
    doc.setFontSize(fs(10));
    const contact = "Cont.Person: —    Cr.Limit: 0.00    Cr.Days: 0";
    doc.text(contact, L, y);
    y += fs(8);

    y += fs(8);
    doc.setDrawColor(200);
    doc.setLineWidth(0.8 * SCALE);
    doc.line(L, y, X2, y);

    // Build rows
    const openDr = Number(openingBalance) >= 0;
    let runBal = Number(openingBalance);
    let totalDr = openDr ? Number(openingBalance) : 0;
    let totalCr = openDr ? 0 : Math.abs(Number(openingBalance));

    const rowsPDF = [];

    rowsPDF.push({
      Date: ddmmyy(fromDate),
      VNo: "",
      Particulars: `${openDr ? "To" : "By"}  Balance b/f`,
      Dr: openDr ? moneyINR(openingBalance) : "",
      Cr: openDr ? "" : moneyINR(Math.abs(openingBalance)),
      Bal: `${moneyINR(Math.abs(runBal))} ${runBal >= 0 ? "Dr" : "Cr"}`,
      BankDt: "",
    });

    transactions.forEach((t) => {
      const dr = Number(t.debit) || 0;
      const cr = Number(t.credit) || 0;
      runBal += dr - cr;
      if (dr) totalDr += dr;
      if (cr) totalCr += cr;

      rowsPDF.push({
        Date: ddmmyy(t.date),
        VNo: cleanRef(t.reference || ""),
        Particulars: particularsLine(t),
        Dr: dr ? moneyINR(dr) : "",
        Cr: cr ? moneyINR(cr) : "",
        Bal: `${moneyINR(Math.abs(runBal))} ${runBal >= 0 ? "Dr" : "Cr"}`,
        BankDt: "",
      });
    });

    const body = rowsPDF.map((r) => [r.Date, r.VNo, r.Particulars, r.Dr, r.Cr, r.Bal, r.BankDt]);

    autoTable(doc, {
      startY: y + fs(15),
      head: [["Date", "V.No.", "Particulars", "Dr.Amount", "Cr.Amount", "Balance"]],
      body,
      theme: "grid",
      styles: { font: hasUnicode ? "NotoSans" : "helvetica", fontSize: fs(7), cellPadding: 5 * SCALE, lineWidth: 0.3 * SCALE, valign: "middle", overflow: "linebreak" },
      headStyles: { fontStyle: "normal", fillColor: [245, 247, 250], textColor: 40, fontSize: fs(7) },
      columnStyles: { 0: { cellWidth: 65 }, 1: { cellWidth: 65 }, 2: { cellWidth: 210 }, 3: { halign: "right", cellWidth: 65 }, 4: { halign: "right", cellWidth: 65 }, 5: { halign: "right", cellWidth: 65 } },
      margin: { left: L, right: R },
      didDrawPage: (d) => {
        doc.setFontSize(fs(9));
        doc.text(`Page ${doc.internal.getNumberOfPages()}`, d.settings.margin.left, doc.internal.pageSize.height - 16 * SCALE);
      },
    });

    const yEnd = (doc.lastAutoTable && doc.lastAutoTable.finalY) || doc.internal.pageSize.getHeight() - 70;
    doc.setFontSize(fs(10));
    doc.text("Total", L, yEnd + fs(20));
    const drX = L + 65 + 65 + 210 + 65;
    const crX = drX + 65;
    doc.text(moneyINR(totalDr), drX, yEnd + fs(20), { align: "right" });
    doc.text(moneyINR(totalCr), crX, yEnd + fs(20), { align: "right" });
    doc.line(L, yEnd + fs(30), X2, yEnd + fs(30));

    const balAsOn = `Balance as on ${dmyPretty(toDate)} :      ${moneyINR(Math.abs(runBal))} ${runBal >= 0 ? "Dr" : "Cr"}`;
    doc.setFontSize(fs(11));
    doc.text(balAsOn, L, yEnd + fs(50));

    const safe = (customerName || `customer_${customerId}`).replace(/[^\w\-]+/g, "_");
    doc.save(`ledger_${safe}_${fromDate}_to_${toDate}.pdf`);
  };

  // ---------- UI ----------
  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto bg-white shadow rounded">
      {/* Title + identity */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Customer Ledger</h2>
          <div className="text-sm text-gray-600 mt-1">
            {customerName ? (
              <span className="bg-gray-100 rounded px-2 py-1">Customer: <b>{customerName}</b></span>
            ) : (
              <span className="bg-gray-100 rounded px-2 py-1">Customer ID: <b>{customerId}</b></span>
            )}
          </div>
        </div>

        {/* Desktop actions */}
        <div className="hidden sm:flex gap-2">
          <button
            onClick={downloadCSV}
            className="px-3 py-2 rounded border border-gray-300 text-gray-800 hover:bg-gray-50"
            disabled={!transactions.length && !openingBalance}
            title="Export CSV"
          >
            Export CSV
          </button>
          <button
            onClick={downloadPDF}
            className={`px-3 py-2 rounded text-white ${
              !transactions.length && !openingBalance ? "bg-gray-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"
            }`}
            disabled={!transactions.length && !openingBalance}
            title="Download PDF"
          >
            Download PDF
          </button>
        </div>
      </div>

      {/* Mobile actions */}
      <div className="sm:hidden grid grid-cols-2 gap-2 mb-3">
        <button
          onClick={downloadCSV}
          className="px-3 py-2 rounded border border-gray-300 text-gray-800"
          disabled={!transactions.length && !openingBalance}
        >
          Export CSV
        </button>
        <button
          onClick={downloadPDF}
          className={`px-3 py-2 rounded text-white ${
            !transactions.length && !openingBalance ? "bg-gray-400" : "bg-green-600"
          }`}
          disabled={!transactions.length && !openingBalance}
        >
          Download PDF
        </button>
      </div>

      {/* Filters row */}
      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div className="w-full sm:w-auto">
          <label className="block text-[12px] text-gray-600 mb-1">From</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="border rounded px-3 py-2 w-full sm:w-[180px]"
          />
        </div>
        <div className="w-full sm:w-auto">
          <label className="block text-[12px] text-gray-600 mb-1">To</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="border rounded px-3 py-2 w-full sm:w-[180px]"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <button onClick={setFY} className="px-3 py-2 rounded border border-gray-300 hover:bg-gray-50">FY</button>
          <button onClick={setThisMonth} className="px-3 py-2 rounded border border-gray-300 hover:bg-gray-50">This Month</button>
          <button onClick={setLastMonth} className="px-3 py-2 rounded border border-gray-300 hover:bg-gray-50">Last Month</button>
          <button onClick={setToday} className="px-3 py-2 rounded border border-gray-300 hover:bg-gray-50">Today</button>
        </div>

        <div className="w-full sm:ml-auto sm:w-auto">
          <label className="block text-[12px] text-gray-600 mb-1">Search</label>
          <input
            type="text"
            placeholder="Type, reference, narration…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border rounded px-3 py-2 w-full sm:w-[260px]"
          />
        </div>

        <button
          onClick={fetchPassbook}
          className="w-full sm:w-auto bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-60"
          disabled={loading}
        >
          {loading ? "Loading…" : "Show Statement"}
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div className="border rounded-lg p-3">
          <div className="text-xs text-gray-500">Opening Balance</div>
          <div className="mt-1 text-lg font-semibold">
            ₹ {prettyAmt(openingBalance).text}{" "}
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ml-1 ${
                badgeToneClass(openingBalance < 0 ? "red" : "blue")
              }`}
            >
              {openingBalance < 0 ? "Cr" : "Dr"}
            </span>
          </div>
        </div>
        <div className="border rounded-lg p-3">
          <div className="text-xs text-gray-500">Total Debits</div>
          <div className="mt-1 text-lg font-semibold">₹ {fmtINR(sumDebit)}</div>
        </div>
        <div className="border rounded-lg p-3">
          <div className="text-xs text-gray-500">Total Credits</div>
          <div className="mt-1 text-lg font-semibold">₹ {fmtINR(sumCredit)}</div>
        </div>
        <div className="border rounded-lg p-3">
          <div className="text-xs text-gray-500">Closing Balance</div>
          <div className="mt-1 text-lg font-semibold">
            ₹ {fmtINR(closingBalance)}{" "}
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ml-1 ${badgeToneClass(
                closingType === "Dr" ? "blue" : "red"
              )}`}
            >
              {closingType}
            </span>
          </div>
        </div>
      </div>

      {/* ===== MOBILE: card list ===== */}
      <div className="sm:hidden space-y-3">
        {/* Opening line */}
        <div className="rounded-xl border border-slate-200 bg-yellow-50 p-3">
          <div className="flex items-center justify-between text-sm font-medium">
            <span>Opening Balance</span>
            <span className="font-mono">₹ {fmtINR(openingBalance)}</span>
          </div>
        </div>

        {/* Transactions */}
        {filtered.map((t, idx) => {
          const tType = String(t.type || "").toUpperCase();
          const tone = typeTone(tType);
          const bal = prettyAmt(t.balance);
          const isDebit = Number(t.debit) > 0;
          const isCredit = Number(t.credit) > 0;

          return (
            <div key={idx} className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900">{toDMY(t.date)}</div>
                  <div className="mt-1">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${badgeToneClass(
                        tone
                      )}`}
                    >
                      {tType || "-"}
                    </span>
                  </div>

                  <div className="mt-2 text-sm">
                    <div className="text-slate-900">{t.reference || "-"}</div>
                    {t.narration ? (
                      <div className="text-slate-600 text-xs mt-0.5 line-clamp-3">{t.narration}</div>
                    ) : null}
                  </div>
                </div>

                <div className="text-right text-sm">
                  <div className={`font-mono ${isDebit ? "text-red-600" : "text-slate-500"}`}>
                    {isDebit ? fmtINR(t.debit) : "-"}
                  </div>
                  <div className={`font-mono ${isCredit ? "text-emerald-700" : "text-slate-500"}`}>
                    {isCredit ? fmtINR(t.credit) : "-"}
                  </div>
                  <div className={`mt-1 font-semibold ${bal.cls}`}>{bal.text}</div>
                </div>
              </div>
            </div>
          );
        })}

        {/* Closing line */}
        {(transactions.length > 0 || openingBalance) && (
          <div className="rounded-xl border border-slate-200 bg-gray-50 p-3">
            <div className="flex items-center justify-between text-sm font-semibold">
              <span>
                Closing Balance <span className="text-gray-500 font-normal">({closingType})</span>
              </span>
              <span className="font-mono">₹ {fmtINR(closingBalance)}</span>
            </div>
          </div>
        )}

        {filtered.length === 0 && !loading && (
          <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-gray-500">
            No transactions found for selected period / filter
          </div>
        )}
      </div>

      {/* ===== DESKTOP/TABLET: table ===== */}
      <div className="hidden sm:block overflow-auto rounded-lg border">
        <table className="min-w-full text-[13px]">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr className="text-gray-600">
              <th className="px-3 py-2 text-left font-medium border-b">Date</th>
              <th className="px-3 py-2 text-left font-medium border-b">Type</th>
              <th className="px-3 py-2 text-left font-medium border-b">Reference / Narration</th>
              <th className="px-3 py-2 text-right font-medium border-b">Debit (₹)</th>
              <th className="px-3 py-2 text-right font-medium border-b">Credit (₹)</th>
              <th className="px-3 py-2 text-right font-medium border-b">Balance (₹)</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            <tr className="bg-yellow-50 font-medium">
              <td className="px-3 py-2" colSpan={5}>Opening Balance</td>
              <td className={`px-3 py-2 ${numClass}`}>₹ {fmtINR(openingBalance)}</td>
            </tr>

            {filtered.map((t, idx) => {
              const tType = String(t.type || "").toUpperCase();
              const tone = typeTone(tType);
              const bal = prettyAmt(t.balance);

              return (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="px-3 py-2">{toDMY(t.date)}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${badgeToneClass(tone)}`}>
                      {tType || "-"}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-gray-900">{t.reference || "-"}</div>
                    {t.narration ? <div className="text-gray-500 text-xs mt-0.5">{t.narration}</div> : null}
                  </td>
                  <td className={`px-3 py-2 ${numClass} ${Number(t.debit) > 0 ? "text-red-600" : ""}`}>
                    {Number(t.debit) > 0 ? fmtINR(t.debit) : "-"}
                  </td>
                  <td className={`px-3 py-2 ${numClass} ${Number(t.credit) > 0 ? "text-emerald-600" : ""}`}>
                    {Number(t.credit) > 0 ? fmtINR(t.credit) : "-"}
                  </td>
                  <td className={`px-3 py-2 ${numClass} ${bal.cls}`}>{bal.text}</td>
                </tr>
              );
            })}

            {(transactions.length > 0 || openingBalance) && (
              <tr className="bg-gray-50 font-semibold">
                <td className="px-3 py-2 text-right" colSpan={5}>
                  Closing Balance <span className="text-gray-500 font-normal">({closingType})</span>
                </td>
                <td className={`px-3 py-2 ${numClass}`}>₹ {fmtINR(closingBalance)}</td>
              </tr>
            )}

            {filtered.length === 0 && !loading && (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-gray-500">
                  No transactions found for selected period / filter
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* subtle footer note */}
      <div className="text-[12px] text-gray-500 mt-3">
        Showing {filtered.length} of {transactions.length} entries. Updated on {toDMY(todayISO())}.
      </div>

      {errorMsg && (
        <div className="mt-3 border border-red-300 bg-red-50 text-red-800 px-3 py-2 rounded">{errorMsg}</div>
      )}
    </div>
  );
}
